/* eslint-disable react-refresh/only-export-components */
import { useEffect, useId, useRef, useState } from 'react'
import { ActionIcon, Anchor, Button, Checkbox, InputBase, Loader } from '@mantine/core'
import { IconBookmark, IconBookmarkFilled, IconCheck } from '@tabler/icons-react'
import { IMaskInput } from 'react-imask'
import type { DiscoveredFacility } from './discoveryTypes'

// Design 1a + 2d ("Decide in the row", combined interior): a dense ranked row
// that expands in place. The expanded interior is a white "Facility details"
// panel (services / insurers as chips, one provenance badge, contact links)
// above a navy confirm bar holding the fax field, its provenance pill and the
// green confirm button. Palette follows the design file.

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

export function todayHours(
  hours: string[] | undefined,
  now: Date = new Date(),
): string | undefined {
  const day = WEEKDAYS[now.getDay()]
  const line = hours?.find((h) => h.startsWith(`${day}:`))
  return line?.slice(day.length + 1).trim() || undefined
}

function formatFax(digits: string): string | undefined {
  // Same rule as the server's normalizeFax: a leading US country code is dropped.
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits
  if (d.length !== 10) return undefined
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`
}

/** A saved fax as stored can carry the country code; give the 10-digit field the real number. */
const initialFax = (fax: string | undefined) =>
  fax ? (formatFax(fax.replace(/\D/g, '')) ?? fax) : ''

/** Pill text next to the fax field: where the number came from, or that there is none. */
export function faxProvenance(f: DiscoveredFacility): string {
  if (!f.fax) return 'no fax on record'
  if (f.faxSource === 'website') return 'unverified · from website'
  if (f.faxSource === 'nppes') return 'from NPI registry'
  if (f.orderCount > 0)
    return `used on ${f.orderCount} previous ${f.orderCount === 1 ? 'order' : 'orders'}`
  return 'saved on file'
}

const PILL =
  'rounded-[5px] px-[7px] py-[3px] text-[11px] font-semibold tracking-[.04em] whitespace-nowrap'
const LABEL = 'flex-none w-[62px] pt-1 text-[12px] text-[#8C9AA8]'
const SERVICE_CHIP =
  'rounded-[13px] bg-[#E9F0F7] px-2.5 py-1 text-[12px] font-medium text-[#103F6B]'
const INSURER_CHIP = 'rounded-[13px] bg-[#F2F5F8] px-2.5 py-1 text-[12px] text-[#4A5A6A]'

export type FacilityRowProps = {
  facility: DiscoveredFacility
  /** 1-based rank shown on the matching map pin. */
  index: number
  selected: boolean
  /** Lets the list measure this row so it can scroll an expanding row into view. */
  rootRef?: (el: HTMLDivElement | null) => void
  onSelect: () => void
  onHover?: (placeId: string | null) => void
  /**
   * `saveFax` — PD-1996: the entered fax is for this order; it is written back to the
   * facility only when the provider ticks "Save fax number for future orders".
   */
  onUse: (fax: string | undefined, saveFax: boolean) => void
  adopting?: boolean
  /**
   * PD-1929 — lab orders are faxed, so "Use this facility" is held until a full fax
   * number is entered; pressing it early marks the fax field in red instead.
   */
  requireFax?: boolean
  /** Chain name in the no-fax explanation, e.g. `Quest Diagnostics`. */
  labName?: string
  /** PD-1938 — switch the order to patient delivery from the no-fax card; offered only when given. */
  onPatientChooses?: () => void
  /**
   * The fax already chosen for this order when this row is the order's facility. The
   * field starts from it, so it shows where the order will actually go (PD-1996: an
   * unsaved fax lives only on the order, not on the facility record).
   */
  orderFax?: string
  /**
   * Adds this facility, with the fax on the field, to the practice's frequently used list.
   * Offered only when given (radiology); like "Use this facility" it needs a complete fax
   * when one is required.
   */
  onRemember?: (fax: string) => void
  remembered?: boolean
  remembering?: boolean
  /** The bookmark star in the header, shown only when `onBookmark` is given. */
  bookmarked?: boolean
  onBookmark?: (bookmarked: boolean) => void
  /** The bookmark save is in flight: the star is disabled so it cannot be toggled twice. */
  bookmarking?: boolean
}

export const FacilityRow = ({
  facility: f,
  index,
  selected,
  rootRef,
  onSelect,
  onHover,
  onUse,
  adopting,
  requireFax,
  labName,
  onPatientChooses,
  orderFax,
  onRemember,
  remembered,
  remembering,
  bookmarked,
  onBookmark,
  bookmarking,
}: FacilityRowProps) => {
  const faxInputId = useId()
  const [fax, setFax] = useState(initialFax(orderFax ?? f.fax))
  const [faxDirty, setFaxDirty] = useState(false)
  const [faxAttempted, setFaxAttempted] = useState(false)
  const [saveFax, setSaveFax] = useState(false)
  const prevPlaceIdRef = useRef(f.placeId)
  useEffect(() => {
    if (prevPlaceIdRef.current !== f.placeId) {
      prevPlaceIdRef.current = f.placeId
      setFax(initialFax(orderFax ?? f.fax))
      setFaxDirty(false)
      setFaxAttempted(false)
      setSaveFax(false)
    } else if (!faxDirty) {
      setFax(initialFax(orderFax ?? f.fax))
    }
  }, [f.placeId, f.fax, orderFax, faxDirty])
  const hours = todayHours(f.hours)
  const faxDigits = fax.replace(/\D/g, '')
  const hasFax = Boolean(f.fax)
  const faxMissing = Boolean(requireFax) && !formatFax(faxDigits)
  const showFaxError = faxAttempted && faxMissing
  // Only a number the facility doesn't already have is worth saving for later.
  const enteredFax = formatFax(faxDigits)
  const offerSave = Boolean(enteredFax) && enteredFax !== initialFax(f.fax)
  const hasFindings =
    f.ai.status === 'ready' && (f.ai.procedures.length > 0 || f.ai.insurances.length > 0)
  const stop = (e: React.SyntheticEvent) => e.stopPropagation()

  return (
    <div
      ref={rootRef}
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      aria-label={`${index}. ${f.name}`}
      onClick={onSelect}
      onMouseEnter={() => onHover?.(f.placeId)}
      onMouseLeave={() => onHover?.(null)}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return
        if (e.key === 'Enter' || e.key === ' ') onSelect()
      }}
      className={`mb-1.5 cursor-pointer overflow-hidden rounded-[10px] border transition-colors ${
        selected
          ? 'border-[#103F6B] bg-[#F7FAFD] shadow-[0_1px_3px_rgba(16,63,107,.08)]'
          : 'border-transparent bg-white hover:bg-[#F7F9FB]'
      }`}
    >
      <div className="flex items-start gap-3 px-4 py-3.5">
        <span
          aria-hidden="true"
          className={`mt-0.5 h-[17px] w-[17px] flex-none rounded-full border ${
            selected ? 'border-[5px] border-[#103F6B]' : 'border-[1.5px] border-[#C3CCD6]'
          }`}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-3">
            <span className="min-w-0 flex-1 text-[15px] font-semibold text-[#12263A]">
              {f.name}
            </span>
            {/* Fixed slot so the NPPES tag lines up across rows regardless of name length. */}
            <span className="flex w-[104px] flex-none justify-end pt-0.5">
              {f.nppes.status === 'matched' && (
                <span
                  className={`${PILL} bg-[#FDF2DA] text-[#8A5A00]`}
                  title={`NPI ${f.nppes.npi}${f.nppes.taxonomy ? ` · ${f.nppes.taxonomy}` : ''}`}
                >
                  NPPES MATCH
                </span>
              )}
              {f.nppes.status === 'pending' && (
                <span className="flex items-center gap-1 text-[11px] text-[#8C9AA8]">
                  <Loader size={10} /> Checking…
                </span>
              )}
            </span>
            {onBookmark && (
              <ActionIcon
                variant="subtle"
                size="sm"
                aria-label={bookmarked ? `Remove bookmark from ${f.name}` : `Bookmark ${f.name}`}
                disabled={bookmarking}
                onClick={(e) => {
                  e.stopPropagation()
                  onBookmark(!bookmarked)
                }}
                onKeyDown={stop}
              >
                {bookmarked ? <IconBookmarkFilled size={16} /> : <IconBookmark size={16} />}
              </ActionIcon>
            )}
            <span className="w-[56px] flex-none text-right text-[14px] font-semibold whitespace-nowrap text-[#12263A]">
              {f.distanceMiles !== undefined && `${f.distanceMiles} mi`}
            </span>
          </div>

          <div className="mt-1.5 flex flex-wrap items-center gap-2.5">
            {f.openNow != null && (
              <span
                className={`${PILL} ${f.openNow ? 'bg-[#E8F6EF] text-[#1E7B45]' : 'bg-[#EDF0F4] text-[#6B7A89]'}`}
              >
                {f.openNow ? 'OPEN NOW' : 'CLOSED'}
              </span>
            )}
            {hours && <span className="text-[13px] text-[#4A5A6A]">{hours}</span>}
            {f.rating != null && (
              <span className="text-[13px] text-[#4A5A6A]">
                ★ {f.rating}
                {f.ratingCount != null ? ` (${f.ratingCount.toLocaleString()})` : ''}
              </span>
            )}
            <span
              className={`${PILL} font-medium ${hasFax ? 'bg-[#E9F0F7] text-[#103F6B]' : 'bg-[#FBEDE3] text-[#9B5B2A]'}`}
            >
              {hasFax ? 'Fax on file' : 'No fax on file'}
            </span>
            {f.orderCount > 0 && (
              <span className={`${PILL} bg-[#E8F6EF] text-[#1E7B45]`}>Used ×{f.orderCount}</span>
            )}
          </div>
          <div className="mt-1 text-[13px] text-[#8C9AA8]">
            {[f.street, f.city, f.state, f.zip].filter(Boolean).join(', ')}
          </div>

          {selected && (
            <div
              className="mt-3.5 overflow-hidden rounded-[9px] border border-[#E3E8EE] bg-white"
              onClick={stop}
              onKeyDown={stop}
            >
              <div className="flex items-center gap-2 border-b border-[#EFF2F6] bg-[#FBFCFD] px-3.5 py-2">
                <span className="text-[12px] font-semibold text-[#4A5A6A]">Facility details</span>
                {f.ai.status === 'pending' && (
                  <span className="ml-auto flex items-center gap-1.5 text-[11px] text-[#6B7A89]">
                    <Loader size={10} /> Checking website…
                  </span>
                )}
                {hasFindings && (
                  <span className="ml-auto flex items-center gap-1.5 rounded-[12px] border border-[#DDE3EA] bg-white px-2 py-[3px] text-[11px] text-[#6B7A89]">
                    <span
                      aria-hidden="true"
                      className="flex h-[13px] w-[13px] items-center justify-center rounded-full border border-[#A8B4C0] text-[9px]"
                    >
                      i
                    </span>
                    AI-sourced from facility website
                  </span>
                )}
              </div>
              <div className="flex flex-col gap-2.5 px-3.5 py-3">
                {f.nppes.status === 'matched' && (
                  <div className="flex flex-wrap items-start gap-2">
                    <span className={LABEL}>NPI</span>
                    <span className="pt-1 text-[12px] text-[#4A5A6A]">
                      {f.nppes.npi}
                      {f.nppes.taxonomy ? ` · ${f.nppes.taxonomy}` : ''}
                      {f.nppes.legalName ? ` · ${f.nppes.legalName}` : ''}
                    </span>
                  </div>
                )}
                {f.ai.procedures.length > 0 && (
                  <div className="flex flex-wrap items-start gap-2">
                    <span className={LABEL}>Services</span>
                    {f.ai.procedures.map((p) => (
                      <span key={p} className={SERVICE_CHIP}>
                        {p}
                      </span>
                    ))}
                  </div>
                )}
                {f.ai.insurances.length > 0 && (
                  <div className="flex flex-wrap items-start gap-2">
                    <span className={LABEL}>Accepts</span>
                    {f.ai.insurances.map((p) => (
                      <span key={p} className={INSURER_CHIP}>
                        {p}
                      </span>
                    ))}
                  </div>
                )}
                <div
                  className={`flex flex-wrap items-center gap-4 text-[13px] ${
                    hasFindings || f.nppes.status === 'matched'
                      ? 'border-t border-[#EFF2F6] pt-2.5'
                      : ''
                  }`}
                >
                  {f.phone && (
                    <Anchor href={`tel:${f.phone.replace(/\D/g, '')}`} size="sm">
                      Call {f.phone}
                    </Anchor>
                  )}
                  {f.website && (
                    <Anchor href={f.website} target="_blank" rel="noreferrer" size="sm">
                      Website
                    </Anchor>
                  )}
                  {/* Frequently used rows are stored favorites with no Maps link. */}
                  {f.mapsUrl && (
                    <Anchor href={f.mapsUrl} target="_blank" rel="noreferrer" size="sm">
                      Directions
                    </Anchor>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {selected && (
        <div
          className="flex flex-wrap items-center gap-3.5 bg-[#103F6B] px-4 py-3"
          onClick={stop}
          onKeyDown={stop}
        >
          <span className="text-[12px] whitespace-nowrap text-[#B8CCE0]">Send order to</span>
          <InputBase
            id={faxInputId}
            aria-label="Order fax"
            error={showFaxError}
            size="sm"
            placeholder="Enter order fax"
            component={IMaskInput}
            mask="(000) 000-0000"
            value={fax}
            onAccept={(value: string, _mask: unknown, event?: Event) => {
              setFax(value)
              if (event) setFaxDirty(true)
            }}
            w={170}
            styles={{
              input: {
                background: 'rgba(255,255,255,.1)',
                border: showFaxError ? '2px solid #FF6B6B' : '1px solid rgba(255,255,255,.28)',
                color: '#fff',
                fontWeight: 500,
              },
            }}
          />
          {showFaxError ? (
            <span role="alert" className="text-[13px] font-semibold text-[#FF8A8A]">
              ← Enter a fax number
            </span>
          ) : (
            <span className="rounded-[12px] border border-white/30 px-2 py-[3px] text-[11px] whitespace-nowrap text-[#B8CCE0]">
              {/* A number the provider typed is not the stored one, so it can't carry the
                  stored number's provenance (e.g. "from NPI registry") — Greptile !2060. */}
              {offerSave ? 'entered for this order' : faxProvenance(f)}
            </span>
          )}
          {onRemember && remembered && (
            // A status in the bar's own pill vocabulary (same as the provenance tag), not a
            // greyed-out button.
            <span
              role="status"
              aria-label={`${f.name} is in frequently used`}
              className="ml-auto flex items-center gap-1 rounded-[12px] border border-white/30 px-2 py-[3px] text-[11px] whitespace-nowrap text-[#DCE7F2]"
            >
              <IconCheck size={12} aria-hidden="true" />
              In frequently used
            </span>
          )}
          {onRemember && !remembered && (
            <Button
              size="sm"
              variant="subtle"
              c="#DCE7F2"
              className="ml-auto"
              loading={remembering}
              onClick={() => {
                if (faxMissing || !enteredFax) {
                  setFaxAttempted(true)
                  document.getElementById(faxInputId)?.focus()
                  return
                }
                onRemember(enteredFax)
              }}
            >
              Add to frequently used
            </Button>
          )}
          <Button
            size="sm"
            color="#2E9E6B"
            className={onRemember ? undefined : 'ml-auto'}
            loading={adopting}
            onClick={() => {
              if (faxMissing) {
                setFaxAttempted(true)
                document.getElementById(faxInputId)?.focus()
                return
              }
              onUse(enteredFax, offerSave && saveFax)
            }}
          >
            Use this facility
          </Button>
          {offerSave && (
            <Checkbox
              className="basis-full"
              size="xs"
              color="#2E9E6B"
              label="Save fax number for future orders"
              checked={saveFax}
              onChange={(e) => setSaveFax(e.currentTarget.checked)}
              styles={{ label: { color: '#DCE7F2' } }}
            />
          )}
        </div>
      )}

      {selected && faxMissing && (
        <div
          role="status"
          className="border-t border-[#F1D9B5] bg-[#FFF7EA] px-4 py-3"
          onClick={stop}
          onKeyDown={stop}
        >
          <div className="text-[14px] font-semibold text-[#7A4A12]">
            {hasFax ? 'Enter a complete fax number' : 'This location has no fax number'}
          </div>
          <p className="mt-1 text-[13px] text-[#5C4630]">
            Enter a fax number to send the order to this facility, or choose a printable order that
            the patient can download and bring to a {labName ?? 'lab'} location.
          </p>
          {/* PD-1938 — on a long list the delivery cards are scrolled away, so the way
              out sits here, at the decision point. */}
          <div className="mt-2.5 flex flex-wrap gap-2">
            {onPatientChooses && (
              <Button size="xs" color="#103F6B" onClick={onPatientChooses}>
                Patient will choose — no fax needed
              </Button>
            )}
            <Button
              size="xs"
              variant="default"
              onClick={() => document.getElementById(faxInputId)?.focus()}
            >
              Enter fax number
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
